import { AxiosInstance } from "axios"
import { syncHttpReq, SyncHttpReqResultStatus } from "@fusion-advantage/formrender"
import { CloudToDeviceBlobApiResponseId, CloudToDeviceBlobKey, CloudToDeviceBlobType, CloudToDeviceBlobUpnAll, deleteCloudToDeviceBlob, selectCloudToDeviceBlob, selectCloudToDeviceBlobEtag, updateCloudToDeviceBlob } from "../../dal/ldbCloudToDeviceBlob"
import { SyncItemStatus, timestamp, WorkItemType } from "@fusion-advantage/formlib"
import { CloudToDeviceAppStatusOk, CloudToDeviceApptBody, CloudToDeviceExportArgs, CloudToDeviceTodoBody, ZCloudToDeviceAppStatusBody, ZCloudToDeviceApptBody, ZCloudToDeviceDownloadFileBody, ZCloudToDeviceOfflineTableDashboardNotificationBody, ZCloudToDeviceOfflineTableDrugProductBody, ZCloudToDeviceOfflineTableDrugProductMapBody, ZCloudToDeviceOfflineTableDrugSearchWordBody, ZCloudToDeviceOfflineTableDrugIcdBody, ZCloudToDeviceOfflineTableMaterialInventoryBody, ZCloudToDeviceTodoBody } from "./ZCloudToDevice"
import { OfflineTableId } from "../../dal/OfflineTable"
import { ldbDrugProductBulkInsert } from "../../dal/ldbDrugProduct"
import { ldbDrugProductMapBulkInsert } from "../../dal/ldbDrugProductMap"
import { ldbDrugSearchWordBulkInsert } from "../../dal/ldbDrugSearchWord"
import { ldbDrugIcdBulkInsert } from "../../dal/ldbDrugIcd"
import { ldbMaterialInventoryBulkInsert } from "../../dal/ldbMaterial"
import { err, ok } from "neverthrow"
import { SubtaskTicker } from "./syncThunk"
import { SyncItemDeferredWriteResult, SyncItemDeferredWriteTask, SyncItemResult, SyncItemSubtaskType } from "./SyncItemResult"
import { getFrontendRequired } from "../../lib/XFrontend"
import { ldbDashboardNotificationBulkInsert } from "../../dal/ldbDashboardNotifications"
import { CodeNext, Export, RExport, safeParseJson, serializeFusionError } from "@fusion-advantage/backcompat"

export const cloudToDeviceSyncLogout = async (): Promise<SyncItemResult<CloudToDeviceAppStatusOk>> => {
	const rDelete = await deleteCloudToDeviceBlob({
		type: CloudToDeviceBlobType.ApiResponse,
		id: CloudToDeviceBlobApiResponseId.AppStatus,
		upn: CloudToDeviceBlobUpnAll,
	})
	if(rDelete.isErr()) {
		return err({
			status: SyncItemStatus.Unknown,
			error: rDelete.error,
		})
	}
	return err({
		status: SyncItemStatus.NoAuth,
	})
}

export const cloudToDeviceSyncAppStatus = async (ticker: SubtaskTicker, axInst: AxiosInstance): Promise<SyncItemResult<CloudToDeviceAppStatusOk>> => {
	const resp = await syncHttpReq({
		axInst,
		method: 'GET',
		url: '/api/v0/status',
	})
	ticker(SyncItemSubtaskType.ApiRequest1)
	if(resp.isErr()) {
		switch(resp.error.status) {
			case SyncHttpReqResultStatus.NetworkError:
				return err({ status: SyncItemStatus.NoConnection })
			default:
				return err({
					status: SyncItemStatus.Unknown,
					error: serializeFusionError('cloudToDeviceSyncAppStatus', resp.error.error)
				})
		}
	}
	const parse = safeParseJson(resp.value.data, ZCloudToDeviceAppStatusBody)
	ticker(SyncItemSubtaskType.Parse)
	if(parse.isErr()) {
		return err({
			status: SyncItemStatus.InvalidBody,
			error: serializeFusionError('cloudToDeviceSyncAppStatus', parse.error),
		})
	}
	const respBody = parse.value
	if(!respBody.Authenticated || !respBody.AuthedIdentity) {
		const rDelete = await deleteCloudToDeviceBlob({
			type: CloudToDeviceBlobType.ApiResponse,
			id: CloudToDeviceBlobApiResponseId.AppStatus,
			upn: CloudToDeviceBlobUpnAll,
		})
		if(rDelete.isErr()) {
			return err({
				status: SyncItemStatus.Unknown,
				error: rDelete.error,
			})
		}
		return err({
			status: SyncItemStatus.NoAuth,
		})
	}
	const rUpdate = await updateCloudToDeviceBlob({
		type: CloudToDeviceBlobType.ApiResponse,
		id: CloudToDeviceBlobApiResponseId.AppStatus,
		upn: CloudToDeviceBlobUpnAll,
		data: new Uint8Array(resp.value.data),
		bin: false,
		etag: null,
		contentType: null,
	})
	ticker(SyncItemSubtaskType.LdbWrite1)
	if(rUpdate.isErr()) {
		return err({
			status: SyncItemStatus.Unknown,
			error: rUpdate.error,
		})
	}
	const rFrontendRequired = getFrontendRequired(resp.value.axResp)
	if(rFrontendRequired.isErr()) {
		return err({
			status: SyncItemStatus.Unknown,
			error: rFrontendRequired.error,
		})
	}
	return ok({
		status: SyncItemStatus.DownloadOk,
		value: {
			...respBody,
			FrontendRequired: rFrontendRequired.value,
		},
	})
}

export const cloudToDeviceSyncEtagTest = async (ticker: SubtaskTicker, axInst: AxiosInstance, etag: string): Promise<SyncItemResult<null>> => {
	const resp = await syncHttpReq({
		axInst,
		// send a POST so we can fully control the If-None-Match headers
		// and receive a 304 transparently passed back from the browser
		method: 'POST',
		url: '/api/v2/offline/etag-test',
		ifNoneMatch: etag,
	})
	ticker(SyncItemSubtaskType.ApiRequest1)
	if(resp.isOk()) {
		// actually, in this test, we explicitly want NotModified
		return err({
			status: SyncItemStatus.Unknown,
			error: serializeFusionError('cloudToDeviceSyncEtagTest', 'Expected 304, Received 200')
		})
	}
	switch(resp.error.status) {
		case SyncHttpReqResultStatus.NetworkError:
			return err({ status: SyncItemStatus.NoConnection })
		case SyncHttpReqResultStatus.NotModified:
			return ok({ status: SyncItemStatus.NotModified, value: null })
		default:
			return err({
				status: SyncItemStatus.Unknown,
				error: serializeFusionError('cloudToDeviceSyncEtagTest', resp.error.error)
			})
	}
}

export const cloudToDeviceSyncOfflineTable = async (ticker: SubtaskTicker, axInst: AxiosInstance, id: OfflineTableId): Promise<SyncItemDeferredWriteResult<null>> => {
	const blobKey: CloudToDeviceBlobKey = {
		type: CloudToDeviceBlobType.OfflineTable,
		id,
		upn: CloudToDeviceBlobUpnAll,
	}
	const rCachedEtag = await selectCloudToDeviceBlobEtag(blobKey)
	ticker(SyncItemSubtaskType.LdbRead1)
	if(rCachedEtag.isErr()) {
		return err({
			status: SyncItemStatus.Unknown,
			error: serializeFusionError('cloudToDeviceSyncOfflineTable', rCachedEtag.error)
		})
	}
	const resp = await syncHttpReq({
		axInst,
		method: 'GET',
		url: '/api/v2/offline/table/' + id,
		ifNoneMatch: rCachedEtag.value,
	})
	ticker(SyncItemSubtaskType.ApiRequest1)
	if(resp.isErr()) {
		switch(resp.error.status) {
			case SyncHttpReqResultStatus.NetworkError:
				return err({ status: SyncItemStatus.NoConnection })
			case SyncHttpReqResultStatus.NotModified:
				return ok({ status: SyncItemStatus.NotModified, value: null })
			default:
				return err({
					status: SyncItemStatus.Unknown,
					error: serializeFusionError('cloudToDeviceSyncOfflineTable', resp.error.error)
				})
		}
	}
	let tableUpdateTask: SyncItemDeferredWriteTask
	// new version, store it
	switch(id) {
		case OfflineTableId.DashboardNotification:
			{
				const parse = safeParseJson(resp.value.data, ZCloudToDeviceOfflineTableDashboardNotificationBody)
				ticker(SyncItemSubtaskType.Parse)
				if(parse.isErr()) {
					return err({
						status: SyncItemStatus.InvalidBody,
						error: serializeFusionError('cloudToDeviceSyncOfflineTable', parse.error)
					})
				}
				tableUpdateTask = async () => {
					const rInsert = await ldbDashboardNotificationBulkInsert(parse.value)
					ticker(SyncItemSubtaskType.LdbWrite1)
					if(rInsert.isErr()) {
						return err({
							status: SyncItemStatus.Unknown,
							error: serializeFusionError('cloudToDeviceSyncOfflineTable', rInsert.error)
						})
					}
					return ok()
				}
			}
			break
		case OfflineTableId.DrugProduct:
			{
				const parse = safeParseJson(resp.value.data, ZCloudToDeviceOfflineTableDrugProductBody)
				ticker(SyncItemSubtaskType.Parse)
				if(parse.isErr()) {
					return err({
						status: SyncItemStatus.InvalidBody,
						error: serializeFusionError('cloudToDeviceSyncOfflineTable', parse.error)
					})
				}
				tableUpdateTask = async () => {
					const rInsert = await ldbDrugProductBulkInsert(parse.value)
					ticker(SyncItemSubtaskType.LdbWrite1)
					if(rInsert.isErr()) {
						return err({
							status: SyncItemStatus.Unknown,
							error: serializeFusionError('cloudToDeviceSyncOfflineTable', rInsert.error)
						})
					}
					return ok()
				}
			}
			break
		case OfflineTableId.DrugProductMap:
			{
				const parse = safeParseJson(resp.value.data, ZCloudToDeviceOfflineTableDrugProductMapBody)
				ticker(SyncItemSubtaskType.Parse)
				if(parse.isErr()) {
					return err({
						status: SyncItemStatus.InvalidBody,
						error: serializeFusionError('cloudToDeviceSyncOfflineTable', parse.error)
					})
				}
				tableUpdateTask = async () => {
					const rInsert = await ldbDrugProductMapBulkInsert(parse.value)
					ticker(SyncItemSubtaskType.LdbWrite1)
					if(rInsert.isErr()) {
						return err({
							status: SyncItemStatus.Unknown,
							error: serializeFusionError('cloudToDeviceSyncOfflineTable', rInsert.error)
						})
					}
					return ok()
				}
			}
			break
		case OfflineTableId.DrugSearchWord:
			{
				const parse = safeParseJson(resp.value.data, ZCloudToDeviceOfflineTableDrugSearchWordBody)
				ticker(SyncItemSubtaskType.Parse)
				if(parse.isErr()) {
					return err({
						status: SyncItemStatus.InvalidBody,
						error: serializeFusionError('cloudToDeviceSyncOfflineTable', parse.error)
					})
				}
				tableUpdateTask = async () => {
					const rInsert = await ldbDrugSearchWordBulkInsert(parse.value)
					ticker(SyncItemSubtaskType.LdbWrite1)
					if(rInsert.isErr()) {
						return err({
							status: SyncItemStatus.Unknown,
							error: serializeFusionError('cloudToDeviceSyncOfflineTable', rInsert.error)
						})
					}
					return ok()
				}
			}
			break
		case OfflineTableId.DrugIcd:
			{
				const parse = safeParseJson(resp.value.data, ZCloudToDeviceOfflineTableDrugIcdBody)
				ticker(SyncItemSubtaskType.Parse)
				if(parse.isErr()) {
					return err({
						status: SyncItemStatus.InvalidBody,
						error: serializeFusionError('cloudToDeviceSyncOfflineTable', parse.error)
					})
				}
				tableUpdateTask = async () => {
					const rInsert = await ldbDrugIcdBulkInsert(parse.value)
					ticker(SyncItemSubtaskType.LdbWrite1)
					if(rInsert.isErr()) {
						return err({
							status: SyncItemStatus.Unknown,
							error: serializeFusionError('cloudToDeviceSyncOfflineTable', rInsert.error)
						})
					}
					return ok()
				}
			}
			break
		case OfflineTableId.MaterialInventory:
			{
				const parse = safeParseJson(resp.value.data, ZCloudToDeviceOfflineTableMaterialInventoryBody)
				ticker(SyncItemSubtaskType.Parse)
				if(parse.isErr()) {
					return err({
						status: SyncItemStatus.InvalidBody,
						error: serializeFusionError('cloudToDeviceSyncOfflineTable', parse.error)
					})
				}
				tableUpdateTask = async () => {
					const rInsert = await ldbMaterialInventoryBulkInsert(parse.value)
					ticker(SyncItemSubtaskType.LdbWrite1)
					if(rInsert.isErr()) {
						return err({
							status: SyncItemStatus.Unknown,
							error: serializeFusionError('cloudToDeviceSyncOfflineTable', rInsert.error)
						})
					}
					return ok()
				}
			}
			break
	}
	const deferredWriteTask: SyncItemDeferredWriteTask = async () => {
		const rTable = await tableUpdateTask()
		if(rTable.isErr()) {
			return err(rTable.error)
		}
		const rUpdate = await updateCloudToDeviceBlob({
			...blobKey,
			data: new Uint8Array(resp.value.data),
			bin: false,
			etag: resp.value.etag,
			contentType: null,
		})
		ticker(SyncItemSubtaskType.LdbWrite2)
		if(rUpdate.isErr()) {
			return err({
				status: SyncItemStatus.Unknown,
				error: serializeFusionError('cloudToDeviceSyncOfflineTable', rUpdate.error)
			})
		}
		return ok()
	}
	return ok({
		status: SyncItemStatus.DownloadOk,
		value: null,
		deferredWriteTask,
	})
}

export const cloudToDeviceSyncTodos = async (ticker: SubtaskTicker, axInst: AxiosInstance, upn: string, deviceToCloudCodeNext: Map<string, CodeNext>): Promise<SyncItemDeferredWriteResult<CloudToDeviceTodoBody>> => {
	const resp = await syncHttpReq({
		axInst,
		method: 'POST',
		url: '/api/v2/dashboard/todos',
		data: {
			DeviceToCloudCodeNext: Object.fromEntries(deviceToCloudCodeNext),
		},
	})
	ticker(SyncItemSubtaskType.ApiRequest1)
	if(resp.isErr()) {
		switch(resp.error.status) {
			case SyncHttpReqResultStatus.NetworkError:
				return err({ status: SyncItemStatus.NoConnection })
			default:
				return err({
					status: SyncItemStatus.Unknown,
					error: serializeFusionError('cloudToDeviceSyncTodos', resp.error.error)
				})
		}
	}
	const parse = safeParseJson(resp.value.data, ZCloudToDeviceTodoBody)
	ticker(SyncItemSubtaskType.Parse)
	if(parse.isErr()) {
		return err({
			status: SyncItemStatus.InvalidBody,
			error: serializeFusionError('cloudToDeviceSyncTodos', parse.error),
		})
	}
	const deferredWriteTask: SyncItemDeferredWriteTask = async () => {
		const rUpdate = await updateCloudToDeviceBlob({
			type: CloudToDeviceBlobType.ApiResponse,
			id: WorkItemType.Todo,
			upn,
			data: new Uint8Array(resp.value.data),
			bin: false,
			etag: null,
			contentType: null,
		})
		ticker(SyncItemSubtaskType.LdbWrite1)
		if(rUpdate.isErr()) {
			return err({
				status: SyncItemStatus.Unknown,
				error: rUpdate.error,
			})
		}
		return ok()
	}
	return ok({
		status: SyncItemStatus.DownloadOk,
		value: parse.value,
		deferredWriteTask,
	})
}

export const cloudToDeviceSyncAppts = async (ticker: SubtaskTicker, axInst: AxiosInstance, upn: string): Promise<SyncItemDeferredWriteResult<CloudToDeviceApptBody>> => {
	const resp = await syncHttpReq({
		axInst,
		method: 'POST',
		url: '/api/v0/dashboard/appointments',
		data: {
			ClientDateTime: timestamp(),
		},
	})
	ticker(SyncItemSubtaskType.ApiRequest1)
	if(resp.isErr()) {
		switch(resp.error.status) {
			case SyncHttpReqResultStatus.NetworkError:
				return err({ status: SyncItemStatus.NoConnection })
			default:
				return err({
					status: SyncItemStatus.Unknown,
					error: serializeFusionError('cloudToDeviceSyncAppts', resp.error.error)
				})
		}
	}
	const parse = safeParseJson(resp.value.data, ZCloudToDeviceApptBody)
	ticker(SyncItemSubtaskType.Parse)
	if(parse.isErr()) {
		return err({
			status: SyncItemStatus.InvalidBody,
			error: serializeFusionError('cloudToDeviceSyncAppts', parse.error)
		})
	}
	const deferredWriteTask: SyncItemDeferredWriteTask = async () => {
		const rUpdate = await updateCloudToDeviceBlob({
			type: CloudToDeviceBlobType.ApiResponse,
			id: WorkItemType.Appointment,
			upn,
			data: new Uint8Array(resp.value.data),
			bin: false,
			etag: null,
			contentType: null,
		})
		ticker(SyncItemSubtaskType.LdbWrite1)
		if(rUpdate.isErr()) {
			return err({
				status: SyncItemStatus.Unknown,
				error: rUpdate.error,
			})
		}
		return ok()
	}
	return ok({
		status: SyncItemStatus.DownloadOk,
		value: parse.value,
		deferredWriteTask,
	})
}

export const cloudToDeviceSyncExport = async (ticker: SubtaskTicker, axInst: AxiosInstance, upn: string, args: CloudToDeviceExportArgs): Promise<SyncItemDeferredWriteResult<Export>> => {
	const blobKey: Omit<CloudToDeviceBlobKey, 'id'> = {
		type: CloudToDeviceBlobType.Export,
		upn,
	}
	let data: ArrayBuffer|Uint8Array|null = null
	let etag: string|null = null
	let modified: boolean
	// [ISSUE-1432] we won't get cache hits on appointments anyway until the
	// first dashboard load, and at that point we will have recorded an
	// apptEncounter relationship and have the EncounterGUID
	if(args.EncounterGUID) {
		const rCached = await selectCloudToDeviceBlob({
			...blobKey,
			id: args.EncounterGUID,
		})
		ticker(SyncItemSubtaskType.LdbRead1)
		if(rCached.isErr()) {
			return err({
				status: SyncItemStatus.Unknown,
				error: serializeFusionError('cloudToDeviceSyncExport', rCached.error),
			})
		}
		if(rCached.value) {
			data = rCached.value.data
			etag = rCached.value.etag
		}
	}
	const resp = await syncHttpReq({
		axInst,
		method: 'POST',
		url: '/api/v0/form/export',
		data: {
			Format: 'v3',
			...args,
		},
		ifNoneMatch: etag,
	})
	ticker(SyncItemSubtaskType.ApiRequest1)
	if(resp.isErr()) {
		switch(resp.error.status) {
			case SyncHttpReqResultStatus.NetworkError:
				return err({ status: SyncItemStatus.NoConnection })
			case SyncHttpReqResultStatus.NotModified:
				if(!data) {
					return err({
						status: SyncItemStatus.Unknown,
						error: serializeFusionError('cloudToDeviceSyncExport', 'returned 304 but no cached data was available'),
					})
				}
				modified = false
				break
			default:
				return err({
					status: SyncItemStatus.Unknown,
					error: serializeFusionError('cloudToDeviceSyncExport', resp.error.error)
				})
		}
	} else {
		data = resp.value.data
		etag = resp.value.etag
		modified = true
	}
	const parse = safeParseJson(data, RExport)
	ticker(SyncItemSubtaskType.Parse)
	if(parse.isErr()) {
		return err({
			status: SyncItemStatus.InvalidBody,
			error: serializeFusionError('cloudToDeviceSyncExport', parse.error),
		})
	}
	let deferredWriteTask: SyncItemDeferredWriteTask|undefined
	if(modified) {
		deferredWriteTask = async () => {
			const encounterGuid = parse.value.Form.Encounter.Encounter_GUID
			const rUpdate = await updateCloudToDeviceBlob({
				...blobKey,
				id: encounterGuid,
				data: new Uint8Array(data),
				bin: false,
				etag,
				contentType: null,
			})
			ticker(SyncItemSubtaskType.LdbWrite1)
			if(rUpdate.isErr()) {
				return err({
					status: SyncItemStatus.Unknown,
					error: rUpdate.error,
				})
			}
			return ok()
		}
	}
	return ok({
		status: modified ? SyncItemStatus.DownloadOk : SyncItemStatus.NotModified,
		value: parse.value,
		deferredWriteTask,
	})
}

// branchInfo: have the api stamp the branch's live renderer info onto the
// flowmap's root tag (branch preview only; see flowmapBranchInfo.ts)
export const cloudToDeviceSyncBranch = async (ticker: SubtaskTicker, axInst: AxiosInstance, branchGuid: string, branchInfo = false): Promise<SyncItemDeferredWriteResult<Uint8Array>> => {
	const blobKey: CloudToDeviceBlobKey = {
		type: CloudToDeviceBlobType.Flowmap,
		id: branchGuid,
		upn: CloudToDeviceBlobUpnAll,
	}
	const rCached = await selectCloudToDeviceBlob(blobKey)
	ticker(SyncItemSubtaskType.LdbRead1)
	if(rCached.isErr()) {
		return err({
			status: SyncItemStatus.Unknown,
			error: serializeFusionError('cloudToDeviceSyncBranch', rCached.error),
		})
	}
	const resp = await syncHttpReq({
		axInst,
		method: 'GET',
		url: '/api/v2/form/flowmap/' + branchGuid + (branchInfo ? '?branchInfo=1' : ''),
		ifNoneMatch: rCached.value?.etag,
	})
	ticker(SyncItemSubtaskType.ApiRequest1)
	if(resp.isErr()) {
		switch(resp.error.status) {
			case SyncHttpReqResultStatus.NetworkError:
				return err({ status: SyncItemStatus.NoConnection })
			case SyncHttpReqResultStatus.NotModified:
				if(!rCached.value?.data) {
					return err({
						status: SyncItemStatus.Unknown,
						error: serializeFusionError('cloudToDeviceSyncBranch', 'returned 304 but no cached data was available'),
					})
				}
				return ok({
					status: SyncItemStatus.NotModified,
					value: rCached.value?.data,
				})
			default:
				return err({
					status: SyncItemStatus.Unknown,
					error: serializeFusionError('cloudToDeviceSyncBranch', resp.error.error)
				})
		}
	}
	const data = new Uint8Array(resp.value.data)
	const deferredWriteTask: SyncItemDeferredWriteTask = async () => {
		const rUpdate = await updateCloudToDeviceBlob({
			...blobKey,
			data: data,
			bin: false,
			etag: resp.value.etag,
			contentType: null,
		})
		ticker(SyncItemSubtaskType.LdbWrite1)
		if(rUpdate.isErr()) {
			return err({
				status: SyncItemStatus.Unknown,
				error: rUpdate.error,
			})
		}
		return ok()
	}
	return ok({
		status: SyncItemStatus.DownloadOk,
		value: data,
		deferredWriteTask,
	})
}

export const cloudToDeviceSyncRenderer = async (ticker: SubtaskTicker, plainAxInst: AxiosInstance, rendererNumber: number): Promise<SyncItemDeferredWriteResult<null>> => {
	const blobKey: CloudToDeviceBlobKey = {
		type: CloudToDeviceBlobType.Renderer,
		id: String(rendererNumber),
		upn: CloudToDeviceBlobUpnAll,
	}
	const rCachedEtag = await selectCloudToDeviceBlobEtag(blobKey)
	ticker(SyncItemSubtaskType.LdbRead1)
	if(rCachedEtag.isErr()) {
		return err({
			status: SyncItemStatus.Unknown,
			error: serializeFusionError('cloudToDeviceSyncRenderer', rCachedEtag.error),
		})
	}
	// these are write-once and really should not be overwritten,
	// so we don't need to re-request
	if(rCachedEtag.value) {
		return ok({
			status: SyncItemStatus.NotModified,
			value: null,
		})
	}
	const resp = await syncHttpReq({
		axInst: plainAxInst,
		method: 'GET',
		url: `https://static.fusion-advantage.com/app-renderer/${rendererNumber}/formrender/formrender.js.gz`,
		// if in the future we want to check the server for updates,
		// but right now these are write-once
		// ifNoneMatch: rCachedEtag.value,
	})
	ticker(SyncItemSubtaskType.ApiRequest1)
	if(resp.isErr()) {
		switch(resp.error.status) {
			case SyncHttpReqResultStatus.NetworkError:
				return err({ status: SyncItemStatus.NoConnection })
			default:
				return err({
					status: SyncItemStatus.Unknown,
					error: serializeFusionError('cloudToDeviceSyncRenderer', resp.error.error)
				})
		}
	}
	const deferredWriteTask: SyncItemDeferredWriteTask = async () => {
		const rUpdate = await updateCloudToDeviceBlob({
			...blobKey,
			data: new Uint8Array(resp.value.data),
			bin: true,
			etag: resp.value.etag,
			contentType: null,
		})
		ticker(SyncItemSubtaskType.LdbWrite1)
		if(rUpdate.isErr()) {
			return err({
				status: SyncItemStatus.Unknown,
				error: rUpdate.error,
			})
		}
		return ok()
	}
	return ok({
		status: SyncItemStatus.DownloadOk,
		value: null,
		deferredWriteTask,
	})
}

export const cloudToDeviceSyncEncounterFile = async (ticker: SubtaskTicker, axInst: AxiosInstance, plainAxInst: AxiosInstance, id: string): Promise<SyncItemDeferredWriteResult<null>> => {
	const blobKey: CloudToDeviceBlobKey = {
		type: CloudToDeviceBlobType.EncounterFile,
		id,
		upn: CloudToDeviceBlobUpnAll,
	}
	const rCachedEtag = await selectCloudToDeviceBlobEtag(blobKey)
	ticker(SyncItemSubtaskType.LdbRead1)
	if(rCachedEtag.isErr()) {
		return err({
			status: SyncItemStatus.Unknown,
			error: serializeFusionError('cloudToDeviceSyncEncounterFile', rCachedEtag.error),
		})
	}
	// these are write-once and really should not be overwritten,
	// so we don't need to re-request
	if(rCachedEtag.value) {
		return ok({
			status: SyncItemStatus.NotModified,
			value: null,
		})
	}
	// call our API to get an Azure SasUrl for the file
	const apiResp = await syncHttpReq({
		axInst,
		method: 'POST',
		url: '/api/v0/azure-blob/download',
		data: {
			BlobName: id,
		},
	})
	ticker(SyncItemSubtaskType.ApiRequest1)
	if(apiResp.isErr()) {
		switch(apiResp.error.status) {
			case SyncHttpReqResultStatus.NetworkError:
				return err({ status: SyncItemStatus.NoConnection })
			default:
				return err({
					status: SyncItemStatus.Unknown,
					error: serializeFusionError('cloudToDeviceSyncEncounterFile', apiResp.error.error)
				})
		}
	}
	const apiParse = safeParseJson(apiResp.value.data, ZCloudToDeviceDownloadFileBody)
	ticker(SyncItemSubtaskType.Parse)
	if(apiParse.isErr()) {
		return err({
			status: SyncItemStatus.InvalidBody,
			error: serializeFusionError('cloudToDeviceSyncEncounterFile', apiParse.error),
		})
	}
	// use SasUrl to download file
	const fileResp = await syncHttpReq({
		axInst: plainAxInst,
		method: 'GET',
		url: apiParse.value.SasUrl,
		// if in the future we want to check the server for updates,
		// but right now these are write-once
		// ifNoneMatch: rCachedEtag.value,
	})
	ticker(SyncItemSubtaskType.ApiRequest2)
	if(fileResp.isErr()) {
		switch(fileResp.error.status) {
			case SyncHttpReqResultStatus.NetworkError:
				return err({ status: SyncItemStatus.NoConnection })
			default:
				return err({
					status: SyncItemStatus.Unknown,
					error: serializeFusionError('cloudToDeviceSyncEncounterFile', fileResp.error.error)
				})
		}
	}
	const deferredWriteTask: SyncItemDeferredWriteTask = async () => {
		const rUpdate = await updateCloudToDeviceBlob({
			...blobKey,
			data: new Uint8Array(fileResp.value.data),
			bin: true,
			etag: fileResp.value.etag,
			contentType: fileResp.value.contentType,
		})
		ticker(SyncItemSubtaskType.LdbWrite1)
		if(rUpdate.isErr()) {
			return err({
				status: SyncItemStatus.Unknown,
				error: rUpdate.error,
			})
		}
		return ok()
	}
	return ok({
		status: SyncItemStatus.DownloadOk,
		value: null,
		deferredWriteTask,
	})
}
